'use client';

import { useState } from 'react';
import { Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/utils';

/**
 * Star rating row. Display mode renders a partial-fill overlay (so 4.3 shows
 * as four-and-a-third stars); interactive mode renders clickable stars with a
 * hover preview. Coral comes from the `text-primary` token — no hardcoded hex.
 */
interface StarsProps {
  /** 0..5. */
  value: number;
  size?: number;
  className?: string;
}

export function Stars({ value, size = 16, className }: StarsProps) {
  const { t } = useTranslation();
  const pct = Math.max(0, Math.min(100, (value / 5) * 100));
  return (
    <span
      className={cn('relative inline-flex leading-none', className)}
      aria-label={t('explore:stars.displayAriaLabel', {
        value: value.toFixed(1),
      })}
    >
      <span className="text-muted-foreground/30 inline-flex">
        {[0, 1, 2, 3, 4].map((i) => (
          <Star key={i} width={size} height={size} aria-hidden />
        ))}
      </span>
      <span
        className="text-primary absolute inset-0 inline-flex overflow-hidden whitespace-nowrap"
        style={{ width: `${pct}%` }}
        aria-hidden
      >
        {[0, 1, 2, 3, 4].map((i) => (
          <Star key={i} width={size} height={size} fill="currentColor" />
        ))}
      </span>
    </span>
  );
}

interface StarPickerProps {
  value: number;
  onChange: (value: number) => void;
  size?: number;
  label?: string;
}

export function StarPicker({
  value,
  onChange,
  size = 28,
  label,
}: StarPickerProps) {
  const { t } = useTranslation();
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  return (
    <div
      className="inline-flex gap-1"
      role="radiogroup"
      aria-label={label ?? t('explore:stars.pickerDefaultLabel')}
      onMouseLeave={() => setHover(0)}
    >
      {[1, 2, 3, 4, 5].map((n) => (
        // eslint-disable-next-line kit-components/use-kit-primitives -- role=radio star toggle; @kit Button's size padding + ghost hover-bg would box the bare star glyph and break the tight rating row
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={value === n}
          aria-label={t('explore:stars.starCount', { count: n })}
          className={cn(
            'rounded-sm transition-transform hover:scale-110',
            n <= shown ? 'text-primary' : 'text-muted-foreground/30',
          )}
          onMouseEnter={() => setHover(n)}
          onClick={() => onChange(n)}
        >
          <Star
            width={size}
            height={size}
            fill={n <= shown ? 'currentColor' : 'none'}
          />
        </button>
      ))}
    </div>
  );
}
